import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';

/**
 * AI Call Simulator (R&D)
 * -----------------------
 * A no-Asterisk bench that plays the citizen side of an IVR Option-5 call in the
 * browser. It is a THIN CLIENT: the browser only ever talks to the hermes-agent.
 * The agent is the brain — it does everything Asterisk + the IVR used to do
 * (speech-to-text, intent/flow reasoning, the Q&A MEMORY / context store,
 * getAnswer, GRMS validation, and text-to-speech) and hands back the spoken
 * reply plus the metadata this screen renders.
 *
 * Feature under test: the Q&A MEMORY. The first time a question is asked the
 * agent hits the real getAnswer engine and, if confident, remembers it; the next
 * time the same/close question is asked the agent answers FROM MEMORY with no
 * getAnswer round-trip. All of that logic lives in the agent — the screen just
 * shows which path was taken.
 */

type Lang = 'ur' | 'en';
type SimulatorBackend = 'preserved-hermes' | 'e4b-intent';
type HermesPersonality =
  | 'default'
  | 'helpful'
  | 'concise'
  | 'technical'
  | 'creative'
  | 'teacher'
  | 'kawaii'
  | 'catgirl'
  | 'pirate'
  | 'shakespeare'
  | 'surfer'
  | 'noir'
  | 'uwu'
  | 'philosopher'
  | 'hype';

/** Live flow state the agent maintains for the call. */
interface FlowState {
  workflow?: string | null;      // null | STATUS | REGISTER
  step?: string | null;
  pendingField?: string | null;
  fields?: Record<string, string | null>;
  complaintNumber?: string | null;
}

/** Shape the hermes-agent gateway returns for one turn. */
interface AgentReply {
  question: string;
  answer: string;
  source: string;
  memoryHit?: boolean;
  similarity?: number | null;
  score?: number | null;
  savedToMemory?: boolean;
  getAnswerCalled?: boolean;
  audioBase64?: string;
  audioType?: string;
  ttsError?: string;
  error?: string;
  flow?: FlowState;
  timings?: Record<string, number>;
}

interface TranscriptionReply {
  success?: boolean;
  sessionId?: string;
  language?: string;
  transcript?: string;
  hermesResponse?: string;
  hermes?: {
    success?: boolean;
    answer?: string;
    error?: string;
    elapsedMs?: number;
    exitCode?: number;
    noSpeech?: boolean;
    hermesSkipped?: boolean;
  };
  provider?: string;
  elapsedMs?: number;
  totalElapsedMs?: number;
  fileName?: string;
  fileSizeBytes?: number;
  audioBase64?: string;
  audioType?: string;
  ttsError?: string;
  ttsMs?: number;
  source?: string;
  route?: {
    similarityScore?: number;
    getAnswerCalled?: boolean;
    matchedQuestion?: string;
  };
  e4bIntent?: {
    intent?: string;
    confidence?: number;
    action?: string;
    elapsedMs?: number;
  };
  error?: string;
}

interface MemoryEntry {
  question: string;
  answer: string;
  score?: number;
  source?: string;
  ts?: number;
}

interface Turn {
  id: number;
  question: string;
  answer: string;
  source: string;
  similarity: number | null;
  score: number | null;
  saved: boolean;
  audioUrl: string;
  timings: Record<string, number>;
  flow: FlowState | null;
  via: 'text' | 'voice';
}

@Component({
  selector: 'app-rnd-ai-call-simulator',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './rnd-ai-call-simulator.component.html',
  styleUrls: ['./rnd-ai-call-simulator.component.scss'],
})
export class RndAiCallSimulatorComponent implements OnInit, OnDestroy {
  language: Lang = 'ur';
  simulatorBackend: SimulatorBackend = 'preserved-hermes';
  personality: HermesPersonality = 'default';
  questionText = '';
  busy = false;
  recording = false;
  recordingStatus = '';
  lastCaptureDebug = '';
  lastCaptureUrl = '';
  errorMessage = '';
  agentOnline: boolean | null = null;
  turns: Turn[] = [];
  memory: MemoryEntry[] = [];
  flow: FlowState | null = null;
  sessionId = `rnd-sim-${Date.now()}`;
  private turnSeq = 0;

  // registration steps, in order, for the live flow tracker
  readonly regSteps: Array<{ key: string; label: string }> = [
    { key: 'citizenName', label: 'Name' },
    { key: 'cnic', label: 'CNIC' },
    { key: 'phone', label: 'Phone' },
    { key: 'tehsil', label: 'Tehsil' },
    { key: 'address', label: 'Address' },
    { key: 'complaintDetails', label: 'Details' },
  ];

  examples = [
    'شکایت کیسے درج کروں؟',
    'گرین سرٹیفکیٹ کیا ہے؟',
    'میری شکایت کی حیثیت کیا ہے؟',
    'How do I register a complaint?',
  ];

  readonly personalities: Array<{ value: HermesPersonality; label: string; description: string }> = [
    { value: 'default', label: 'Saba default', description: 'Use only Saba SOUL.md tone' },
    { value: 'helpful', label: 'Helpful', description: 'Friendly, general-purpose assistant' },
    { value: 'concise', label: 'Concise', description: 'Brief, to-the-point responses' },
    { value: 'technical', label: 'Technical', description: 'Detailed, accurate technical expert' },
    { value: 'creative', label: 'Creative', description: 'Innovative, outside-the-box thinking' },
    { value: 'teacher', label: 'Teacher', description: 'Patient educator with clear examples' },
    { value: 'kawaii', label: 'Kawaii', description: 'Cute expressions and enthusiasm' },
    { value: 'catgirl', label: 'Catgirl', description: 'Neko-style tone' },
    { value: 'pirate', label: 'Pirate', description: 'Captain Hermes style' },
    { value: 'shakespeare', label: 'Shakespeare', description: 'Bardic prose' },
    { value: 'surfer', label: 'Surfer', description: 'Chill surfer tone' },
    { value: 'noir', label: 'Noir', description: 'Detective narration' },
    { value: 'uwu', label: 'Uwu', description: 'Maximum cute tone' },
    { value: 'philosopher', label: 'Philosopher', description: 'Reflective style' },
    { value: 'hype', label: 'Hype', description: 'High-energy enthusiasm' },
  ];

  readonly simulatorBackends: Array<{ value: SimulatorBackend; label: string; description: string }> = [
    {
      value: 'preserved-hermes',
      label: 'Preserved Hermes',
      description: 'Existing R&D simulator behavior; kept unchanged for demo/rollback comparison.',
    },
    {
      value: 'e4b-intent',
      label: 'E4B Intent POC',
      description: 'New isolated path: Whisper transcription, E4B intent classification first, then routed action.',
    },
  ];

  private mediaStream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private mimeType = '';
  private recordingStartedAt = 0;
  private lastRecordingMs = 0;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private audioMeterFrame = 0;
  private audioMeterLastAt = 0;
  private audioPeakRms = 0;
  private audioVoicedMs = 0;
  private audioMeterElapsedMs = 0;
  private audioNoiseRmsTotal = 0;
  private audioNoiseRmsSamples = 0;
  private effectiveVoiceRmsThreshold = 0.035;
  private readonly minVoiceRecordingMs = 1800;
  private readonly minVoiceBlobBytes = 3500;
  private readonly minVoiceActiveMs = 400;
  private readonly noiseCalibrationMs = 350;
  private readonly voiceRmsThreshold = 0.035;

  ngOnInit(): void {
    this.pingAgent();
    this.loadMemory();
  }

  ngOnDestroy(): void {
    this.stopTracks();
    this.turns.forEach(t => t.audioUrl && URL.revokeObjectURL(t.audioUrl));
  }

  // ---- endpoints ----------------------------------------------------------
  private get agentBase(): string {
    return (environment.hermesAgentUrl || '').replace(/\/$/, '');
  }

  private get hermesAgentBackendBase(): string {
    return `${environment.apiHost.replace(/\/$/, '')}/api/v1/rnd/hermes-agent`;
  }

  private get e4bIntentBackendBase(): string {
    return `${environment.apiHost.replace(/\/$/, '')}/api/v1/rnd/e4b-intent-simulator`;
  }

  private get currentBackendBase(): string {
    return this.simulatorBackend === 'e4b-intent'
      ? this.e4bIntentBackendBase
      : this.hermesAgentBackendBase;
  }

  private get currentBackendLabel(): string {
    return this.simulatorBackend === 'e4b-intent' ? 'E4B intent simulator' : 'hermes-agent';
  }

  async pingAgent(): Promise<void> {
    try {
      const res = await fetch(`${this.currentBackendBase}/health`, {
        method: 'GET',
        headers: this.authHeaders(),
      });
      this.agentOnline = res.ok;
    } catch {
      this.agentOnline = false;
    }
  }

  private async loadMemory(): Promise<void> {
    try {
      const res = await fetch(`${this.agentBase}/memory`, { method: 'GET' });
      if (!res.ok) return;
      const j: any = await res.json();
      this.memory = Array.isArray(j?.items) ? j.items : Array.isArray(j) ? j : [];
    } catch { /* agent may not expose /memory yet */ }
  }

  /** The one call the browser makes. Everything else is the agent's job. */
  private async callAgent(payload: Record<string, unknown>): Promise<AgentReply> {
    if (this.simulatorBackend === 'e4b-intent') {
      return this.callE4bIntentBackend(payload);
    }
    const res = await fetch(`${this.agentBase}/ask`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...payload,
        language: this.language,
        sessionId: this.sessionId,
        personality: this.personality,
      }),
    });
    if (!res.ok) throw new Error(`hermes-agent HTTP ${res.status}`);
    return res.json();
  }

  private async callE4bIntentBackend(payload: Record<string, unknown>): Promise<AgentReply> {
    const res = await fetch(`${this.e4bIntentBackendBase}/text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeaders(),
      },
      body: JSON.stringify({
        text: payload['text'] || '',
        language: this.language,
        sessionId: this.sessionId,
        personality: this.personality,
        context: {
          platform: 'AI_CALL_SIMULATOR',
          mode: 'E4B_INTENT',
        },
      }),
    });
    if (!res.ok) throw new Error(`E4B intent simulator HTTP ${res.status}`);
    const json: any = await res.json();
    const r: TranscriptionReply = json?.data || json;
    return this.backendReplyToAgentReply(r, String(payload['text'] || ''));
  }

  // ---- text turn ----------------------------------------------------------
  useExample(text: string): void { this.questionText = text; }

  async askText(): Promise<void> {
    const q = this.questionText.trim();
    if (!q || this.busy) return;
    this.questionText = '';
    await this.runTurn({ text: q }, 'text', {});
  }

  // ---- voice turn (push-to-talk) -----------------------------------------
  async toggleRecording(): Promise<void> {
    if (this.recording) { this.stopRecording(); return; }
    if (this.busy) return;
    this.errorMessage = '';
    this.lastCaptureDebug = '';
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
      this.mimeType = this.pickMimeType();
      this.recorder = new MediaRecorder(this.mediaStream, this.mimeType ? { mimeType: this.mimeType } : undefined);
      this.chunks = [];
      this.recorder.addEventListener('dataavailable', e => { if (e.data?.size) this.chunks.push(e.data); });
      this.recorder.addEventListener('stop', () => this.onRecordingStopped());
      this.startAudioMeter(this.mediaStream);
      this.recorder.start(250);
      this.recordingStartedAt = performance.now();
      this.lastRecordingMs = 0;
      this.recording = true;
      this.recordingStatus = this.language === 'ur'
        ? 'سن رہی ہوں… مکمل جملہ بول کر Stop & send دبائیں۔'
        : 'Listening… say the full sentence, then press Stop & send.';
    } catch (err) {
      this.errorMessage = err instanceof Error ? err.message : 'Microphone access failed.';
      this.stopTracks();
    }
  }

  private stopRecording(): void {
    this.lastRecordingMs = this.recordingStartedAt ? Math.round(performance.now() - this.recordingStartedAt) : 0;
    if (this.lastRecordingMs < this.minVoiceRecordingMs) {
      this.recordingStatus = this.language === 'ur'
        ? 'بس ایک لمحہ، آواز مکمل ریکارڈ ہو رہی ہے…'
        : 'One moment, finishing the recording…';
      window.setTimeout(() => this.stopRecording(), this.minVoiceRecordingMs - this.lastRecordingMs);
      return;
    }
    this.recordingStatus = this.language === 'ur' ? 'آواز بھیج رہی ہوں…' : 'Sending audio…';
    if (this.recorder && this.recorder.state !== 'inactive') {
      this.recorder.requestData();
      this.recorder.stop();
    }
    this.recording = false;
  }

  private async onRecordingStopped(): Promise<void> {
    const peakRms = this.audioPeakRms;
    const voicedMs = Math.round(this.audioVoicedMs);
    const rmsThreshold = this.effectiveVoiceRmsThreshold;
    this.stopTracks();
    const blob = new Blob(this.chunks, { type: this.mimeType || 'audio/webm' });
    const recordingMs = this.lastRecordingMs || (this.recordingStartedAt ? Math.round(performance.now() - this.recordingStartedAt) : 0);
    this.recordingStartedAt = 0;
    this.lastRecordingMs = 0;
    this.recordingStatus = '';
    this.setLastCapturePreview(blob, recordingMs, voicedMs, peakRms, rmsThreshold);
    if (!blob.size) {
      this.errorMessage = this.language === 'ur'
        ? 'آواز ریکارڈ نہیں ہوئی۔ Speak دبائیں، مکمل بات کریں، پھر Stop & send دبائیں۔'
        : 'No audio was captured. Please click Speak, talk, then click Stop & send.';
      return;
    }
    if (recordingMs < this.minVoiceRecordingMs || blob.size < this.minVoiceBlobBytes) {
      this.errorMessage = this.language === 'ur'
        ? `ریکارڈنگ بہت مختصر تھی (${recordingMs}ms, ${blob.size} bytes)۔ Speak دبائیں، پورا جملہ بولیں، پھر Stop & send دبائیں۔`
        : `Recording was too short (${recordingMs}ms, ${blob.size} bytes). Please click Speak, say the full sentence, then click Stop & send.`;
      return;
    }
    if (voicedMs < this.minVoiceActiveMs || peakRms < rmsThreshold) {
      this.errorMessage = this.language === 'ur'
        ? 'مائیک نے واضح آواز محسوس نہیں کی۔ براہِ کرم مائیک کے قریب آ کر دوبارہ بولیں۔'
        : 'The microphone did not detect clear speech. Please move closer to the mic and try again.';
      return;
    }
    await this.runTranscription(blob);
  }

  // ---- render a turn from the agent reply ---------------------------------
  private async runTranscription(blob: Blob): Promise<void> {
    this.busy = true;
    this.errorMessage = '';
    try {
      const form = new FormData();
      form.append('file', blob, `hermes-agent-${Date.now()}.${this.fileExtension()}`);
      form.append('language', this.language);
      form.append('sessionId', this.sessionId);
      form.append('personality', this.personality);
      form.append('speakerLabel', 'citizen');
      form.append('sequenceNo', String(this.turnSeq + 1));
      form.append('startMs', '0');

      const res = await fetch(`${this.currentBackendBase}/transcribe`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: form,
      });
      if (!res.ok) throw new Error(`${this.currentBackendLabel} transcription HTTP ${res.status}`);
      const json: any = await res.json();
      const r: TranscriptionReply = json?.data || json;
      if (r.error) { this.errorMessage = r.error; return; }

      const transcript = (r.transcript || '').trim();
      const noSpeech = !transcript || !!r.hermes?.noSpeech || !!r.hermes?.hermesSkipped;
      if (noSpeech) {
        this.errorMessage = this.language === 'ur'
          ? 'آواز واضح موصول نہیں ہوئی، براہِ کرم دوبارہ بولیں۔'
          : 'No clear speech was received. Please speak again.';
        return;
      }
      const hermesSuccess = r.hermes?.success !== false;
      const hermesText = (r.hermesResponse || r.hermes?.answer || '').trim();
      const hermesError = r.hermes?.error || '';
      let audioUrl = '';
      if (r.audioBase64) {
        const bytes = Uint8Array.from(atob(r.audioBase64), c => c.charCodeAt(0));
        audioUrl = URL.createObjectURL(new Blob([bytes], { type: r.audioType || 'audio/mpeg' }));
      }
      const turn: Turn = {
        id: ++this.turnSeq,
        question: transcript,
        answer: hermesText || hermesError || '(empty Hermes response)',
        source: r.source || (hermesSuccess && hermesText ? 'HERMES_TEXT' : 'HERMES_FAILED'),
        similarity: r.route?.similarityScore ?? null,
        score: r.e4bIntent?.confidence ?? null,
        saved: false,
        audioUrl,
        timings: {
          ...(r.elapsedMs !== undefined ? { transcribeMs: r.elapsedMs } : {}),
          ...(r.hermes?.elapsedMs !== undefined ? { hermesMs: r.hermes.elapsedMs } : {}),
          ...(r.e4bIntent?.elapsedMs !== undefined ? { e4bIntentMs: r.e4bIntent.elapsedMs } : {}),
          ...(r.ttsMs !== undefined ? { ttsMs: r.ttsMs } : {}),
          ...(r.totalElapsedMs !== undefined ? { overallMs: r.totalElapsedMs } : {}),
        },
        flow: null,
        via: 'voice',
      };
      this.turns = [turn, ...this.turns];
      if (audioUrl) this.play(audioUrl);
    } catch (err) {
      this.errorMessage = err instanceof Error ? err.message : 'hermes-agent transcription failed.';
    } finally {
      this.busy = false;
    }
  }

  private async runTurn(payload: Record<string, unknown>, via: 'text' | 'voice', _seed: Record<string, number>): Promise<void> {
    this.busy = true;
    this.errorMessage = '';
    try {
      const r = await this.callAgent(payload);
      if (r.error) { this.errorMessage = r.error; return; }

      let audioUrl = '';
      if (r.audioBase64) {
        const bytes = Uint8Array.from(atob(r.audioBase64), c => c.charCodeAt(0));
        audioUrl = URL.createObjectURL(new Blob([bytes], { type: r.audioType || 'audio/mpeg' }));
      }

      const turn: Turn = {
        id: ++this.turnSeq,
        question: r.question || '(no question)',
        answer: r.answer || '',
        source: r.source || 'QNA',
        similarity: r.similarity ?? null,
        score: r.score ?? null,
        saved: !!r.savedToMemory,
        audioUrl,
        timings: r.timings || {},
        flow: r.flow || null,
        via,
      };
      this.turns = [turn, ...this.turns];
      this.flow = r.flow || this.flow;
      if (audioUrl) this.play(audioUrl);
      if (r.savedToMemory || r.source === 'GET_ANSWER') this.loadMemory();
    } catch (err) {
      this.errorMessage = err instanceof Error ? err.message : 'hermes-agent request failed.';
    } finally {
      this.busy = false;
    }
  }

  private backendReplyToAgentReply(r: TranscriptionReply, fallbackQuestion: string): AgentReply {
    return {
      question: r.transcript || fallbackQuestion,
      answer: r.hermesResponse || r.hermes?.answer || r.error || '',
      source: r.source || 'E4B_INTENT',
      similarity: r.route?.similarityScore ?? null,
      score: r.e4bIntent?.confidence ?? null,
      getAnswerCalled: r.route?.getAnswerCalled,
      audioBase64: r.audioBase64,
      audioType: r.audioType,
      ttsError: r.ttsError,
      error: r.error,
      timings: {
        ...(r.elapsedMs !== undefined ? { transcribeMs: r.elapsedMs } : {}),
        ...(r.hermes?.elapsedMs !== undefined ? { e4bIntentMs: r.hermes.elapsedMs } : {}),
        ...(r.e4bIntent?.elapsedMs !== undefined ? { e4bIntentMs: r.e4bIntent.elapsedMs } : {}),
        ...(r.ttsMs !== undefined ? { ttsMs: r.ttsMs } : {}),
        ...(r.totalElapsedMs !== undefined ? { overallMs: r.totalElapsedMs } : {}),
      },
    };
  }

  // ---- memory panel (read-only view of the agent's store) -----------------
  async clearMemory(): Promise<void> {
    try {
      await fetch(`${this.agentBase}/memory`, { method: 'DELETE' });
      this.memory = [];
    } catch (err) {
      this.errorMessage = err instanceof Error ? err.message : 'Could not clear agent memory.';
    }
  }

  clearConversation(): void {
    this.turns.forEach(t => t.audioUrl && URL.revokeObjectURL(t.audioUrl));
    this.turns = [];
  }

  clearCapturePreview(): void {
    if (this.lastCaptureUrl) URL.revokeObjectURL(this.lastCaptureUrl);
    this.lastCaptureUrl = '';
    this.lastCaptureDebug = '';
  }

  // ---- source badge presentation ------------------------------------------
  sourceKind(source: string): 'memory' | 'live' | 'status' | 'register' | 'agent' | 'other' {
    if (source === 'QNA_MEMORY') return 'memory';
    if (source === 'QNA_GETANSWER') return 'live';
    if (source === 'E4B_INTENT_GETANSWER') return 'live';
    if (source === 'NO_SPEECH') return 'other';
    if (source === 'HERMES_TEXT' || source === 'HERMES_FAILED') return 'agent';
    if (source.startsWith('E4B_INTENT_CHECK_COMPLAINT_STATUS')) return 'status';
    if (source.startsWith('E4B_INTENT_REGISTER_COMPLAINT')) return 'register';
    if (source.startsWith('E4B_INTENT')) return 'agent';
    if (source.startsWith('STATUS')) return 'status';
    if (source.startsWith('REGISTER')) return 'register';
    if (source === 'AGENT') return 'agent';
    return 'other';
  }

  sourceLabel(source: string): string {
    const map: Record<string, string> = {
      QNA_MEMORY: '🧠 from memory',
      QNA_GETANSWER: '⚡ getAnswer',
      STATUS: 'status',
      STATUS_OTP_SENT: 'OTP sent',
      STATUS_VERIFIED: '✓ status verified',
      STATUS_NOT_FOUND: 'not found',
      STATUS_OTP_INVALID: 'wrong OTP',
      STATUS_FAILED: 'service error',
      REGISTER: 'registration',
      REGISTER_FIELD_OK: '✓ field validated',
      REGISTER_INVALID: 'field rejected',
      REGISTER_BACKTRACK: '↩ backtrack',
      REGISTER_DONE: '✓ registered',
      REGISTER_PENDING: 'submitted',
      REGISTER_FAILED: 'registration failed',
      AGENT: 'to agent',
      HERMES_TEXT: 'Saba Response',
      HERMES_FAILED: 'Hermes failed',
      E4B_INTENT: 'E4B intent',
      E4B_INTENT_GETANSWER: 'E4B → getAnswer',
      E4B_INTENT_QNA_LOW_CONFIDENCE: 'E4B QNA low confidence',
      E4B_INTENT_QNA_ERROR: 'E4B QNA error',
      E4B_INTENT_REGISTER_COMPLAINT: 'E4B registration',
      E4B_INTENT_CHECK_COMPLAINT_STATUS: 'E4B status',
      E4B_INTENT_GREETING: 'E4B greeting',
      E4B_INTENT_LIVE_AGENT: 'E4B handoff',
      E4B_INTENT_CANCEL: 'E4B cancel',
      E4B_INTENT_UNKNOWN: 'E4B clarify',
      NO_SPEECH: 'No speech detected',
    };
    return map[source] || source.toLowerCase().replace(/_/g, ' ');
  }

  fieldState(key: string): 'done' | 'active' | 'todo' {
    if (this.flow?.fields && key in this.flow.fields) return 'done';
    if (this.flow?.pendingField === key) return 'active';
    return 'todo';
  }

  personalityDescription(): string {
    return this.personalities.find(p => p.value === this.personality)?.description || '';
  }

  simulatorBackendDescription(): string {
    return this.simulatorBackends.find(b => b.value === this.simulatorBackend)?.description || '';
  }

  // ---- helpers ------------------------------------------------------------
  private play(url: string): void {
    try { new Audio(url).play().catch(() => {}); } catch {}
  }

  replay(turn: Turn): void { if (turn.audioUrl) this.play(turn.audioUrl); }

  replayCapture(): void { if (this.lastCaptureUrl) this.play(this.lastCaptureUrl); }

  private pickMimeType(): string {
    return ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg']
      .find(t => MediaRecorder.isTypeSupported(t)) || '';
  }

  private startAudioMeter(stream: MediaStream): void {
    this.stopAudioMeter();
    this.audioPeakRms = 0;
    this.audioVoicedMs = 0;
    this.audioMeterElapsedMs = 0;
    this.audioNoiseRmsTotal = 0;
    this.audioNoiseRmsSamples = 0;
    this.effectiveVoiceRmsThreshold = this.voiceRmsThreshold;
    this.audioMeterLastAt = performance.now();
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    try {
      this.audioContext = new AudioCtx();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 1024;
      const source = this.audioContext.createMediaStreamSource(stream);
      source.connect(this.analyser);
      const data = new Uint8Array(this.analyser.fftSize);
      const tick = () => {
        if (!this.analyser) return;
        const now = performance.now();
        const delta = Math.max(0, now - this.audioMeterLastAt);
        this.audioMeterLastAt = now;
        this.analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const centered = (data[i] - 128) / 128;
          sum += centered * centered;
        }
        const rms = Math.sqrt(sum / data.length);
        this.audioPeakRms = Math.max(this.audioPeakRms, rms);
        this.audioMeterElapsedMs += delta;
        if (this.audioMeterElapsedMs <= this.noiseCalibrationMs) {
          this.audioNoiseRmsTotal += rms;
          this.audioNoiseRmsSamples += 1;
        }
        const noiseRms = this.audioNoiseRmsSamples ? this.audioNoiseRmsTotal / this.audioNoiseRmsSamples : 0;
        this.effectiveVoiceRmsThreshold = Math.max(this.voiceRmsThreshold, (noiseRms * 2.2) + 0.01);
        if (this.audioMeterElapsedMs > this.noiseCalibrationMs && rms >= this.effectiveVoiceRmsThreshold) this.audioVoicedMs += delta;
        this.audioMeterFrame = window.requestAnimationFrame(tick);
      };
      this.audioMeterFrame = window.requestAnimationFrame(tick);
    } catch {
      this.stopAudioMeter();
    }
  }

  private stopAudioMeter(): void {
    if (this.audioMeterFrame) {
      window.cancelAnimationFrame(this.audioMeterFrame);
      this.audioMeterFrame = 0;
    }
    this.analyser = null;
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
  }

  private fileExtension(): string {
    return this.mimeType.includes('ogg') ? 'ogg' : 'webm';
  }

  private setLastCapturePreview(blob: Blob, recordingMs: number, voicedMs: number, peakRms: number, threshold: number): void {
    if (this.lastCaptureUrl) URL.revokeObjectURL(this.lastCaptureUrl);
    this.lastCaptureUrl = blob.size ? URL.createObjectURL(blob) : '';
    this.lastCaptureDebug = [
      `duration=${recordingMs}ms`,
      `bytes=${blob.size}`,
      `voice=${voicedMs}ms`,
      `peak=${peakRms.toFixed(3)}`,
      `threshold=${threshold.toFixed(3)}`,
    ].join(' ? ');
  }

  private authHeaders(): Record<string, string> {
    const token = localStorage.getItem('auth-token') ?? '';
    return {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  private stopTracks(): void {
    this.stopAudioMeter();
    this.mediaStream?.getTracks().forEach(t => t.stop());
    this.mediaStream = null;
  }

  pct(v: number | null | undefined): string {
    return v === null || v === undefined ? '-' : `${Math.round(v * 100)}%`;
  }

  timingList(t: Record<string, number>): Array<{ k: string; v: number }> {
    const order = ['transcribeMs', 'hermesMs', 'overallMs', 'memoryLookupMs', 'lookupMs', 'getAnswerMs', 'ttsMs'];
    return order.filter(k => k in t).map(k => ({ k: k.replace(/Ms$/, ''), v: t[k] }));
  }

  totalMs(t: Record<string, number>): number {
    if (typeof t['overallMs'] === 'number') return t['overallMs'];
    return Object.entries(t)
      .filter(([k]) => k !== 'overallMs')
      .reduce((a, [, b]) => a + (typeof b === 'number' ? b : 0), 0);
  }
}
